import {
  Stack,
  Drawer,
  List,
  IconButton,
  AppBar,
  Collapse,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

import { useWelcomeQuery } from '../../../api/getWelcomeDetails';
import useLogoutMutation from '../../../api/logout';
import ChevronDownIcon from '../../../assets/icons/ChevronDown';
import ChevronUpIcon from '../../../assets/icons/ChevronUp';
import CloseIcon from '../../../assets/icons/Close';
import Logo from '../../../assets/icons/Logo';
import MenuIcon from '../../../assets/icons/Menu';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import { DrawerItem, drawerMap } from '../constants';
import ListIcons, { ListIconsProps } from './ListIcons';
import useGetFeatureFlags from '../../../hooks/useGetFeatureFlags';

const Header = () => {
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<
    Map<string, boolean>
  >(new Map());

  const { data: welcomeData } = useWelcomeQuery();
  const logoutMutation = useLogoutMutation();
  const { enableSettings } = useGetFeatureFlags();

  const showPasswordSettings =
    enableSettings && welcomeData?.userInfo.loginType === 'cloud';

  const renderDrawerItem = ({ path, ...item }: DrawerItem, index: number) => {
    const key = `${item.text}-${index}`;
    const propsToSend: ListIconsProps = { ...item };

    if (item.subCategory) {
      propsToSend.onClick = () =>
        setExpandedSections((prev) => {
          const newMap = new Map(prev);
          newMap.forEach((value, mapKey) => {
            newMap.set(mapKey, false);
          });
          newMap.set(item.text, !(prev.get(item.text) || false));
          return newMap;
        });
      propsToSend.arrow = expandedSections.get(item.text) ? (
        <ChevronUpIcon />
      ) : (
        <ChevronDownIcon />
      );
    }

    if (path) {
      propsToSend.path = path;
      propsToSend.onClick = () => {
        track(`${item.text} Accessed`, {
          event_type: 'Link Accessed',
          location: 'Left Navigational Pane',
          link: item.text,
        });
        setOpen(false);
      };
    }
    return (
      <Fragment key={key}>
        <ListIcons {...propsToSend} showText={open} data-testid={key} />
        {!!item.subCategory && renderChildList({ path, ...item }, index)}
      </Fragment>
    );
  };

  const renderChildList = ({ path, ...item }: DrawerItem, index: number) => {
    if (!item.subCategory) {
      return null;
    }
    return (
      <Collapse
        key={`${item.text}-${index}-children`}
        component="li"
        in={(expandedSections.get(item.text) || false) && open}
        timeout="auto"
        unmountOnExit
      >
        <List sx={{ pl: 4 }}>
          {item.subCategory?.map((child, childIndex) =>
            renderDrawerItem(child, +`${index}${childIndex}`)
          )}
        </List>
      </Collapse>
    );
  };

  const toggleDrawer =
    (bool: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }

      setOpen(bool);
    };

  return (
    <AppBar
      sx={{ backgroundColor: colors.csaaWhite, displayPrint: 'none' }}
      position="sticky"
      color="default"
    >
      <Stack direction="row" sx={{ m: 0 }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={toggleDrawer(true)}
          edge="start"
          sx={{
            m: 2,
            p: 0.5,
            '&:focus': {
              outline: `solid 2px ${colors.csaaBlack}`,
              outlineOffset: '1px',
              borderRadius: 2,
              backgroundColor: 'inherit',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <IconButton
          component={Link}
          aria-label="Home"
          disableRipple
          to="/"
          onClick={() => {
            track('Overview Accessed', {
              event_type: 'Link Accessed',
              location: 'Left Navigational Pane',
              link: 'Logo',
            });
          }}
          sx={{
            p: 0,
            justifyContent: 'inherit',
            my: 2,
            '&:focus': {
              outline: `solid 2px ${colors.csaaBlack}`,
              outlineOffset: '4px',
              borderRadius: 2,
              backgroundColor: 'inherit',
            },
          }}
        >
          <Logo
            sx={{
              height: 24,
              width: 47,
            }}
          />
        </IconButton>
      </Stack>
      <Drawer
        anchor="left"
        open={open}
        onClose={toggleDrawer(false)}
        sx={{
          display: { desktop: 'none' },
          width: 1,
          flexShrink: 0,
          '& .MuiBackdrop-root': { backdropFilter: 'blur(5px)' },
          '& .MuiDrawer-paper': {
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
            height: { mobile: 1 },
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '0px 8px 16px rgba(25, 30, 43, 0.16)',
          },
        }}
      >
        <Stack sx={{ height: 1 }} direction="column">
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ p: 2, pb: 4 }}
          >
            <IconButton
              component={Link}
              aria-label="Home"
              disableRipple
              to="/"
              onClick={() => {
                track('Overview Accessed', {
                  event_type: 'Link Accessed',
                  location: 'Left Navigational Pane',
                  link: 'Logo',
                });
              }}
              sx={{
                p: 0,
                justifyContent: 'inherit',
              }}
            >
              <Logo
                sx={{
                  height: 24,
                  width: 47,
                }}
              />
            </IconButton>
            <IconButton
              aria-label="close"
              color="inherit"
              size="medium"
              data-testid="errorSnackbarIconButton"
              onClick={toggleDrawer(false)}
              sx={{ p: 0 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack
            sx={{ px: 2, pb: 3, height: 1 }}
            justifyContent="space-between"
          >
            <List>
              {[
                drawerMap.Home,
                drawerMap.Bill,
                drawerMap.Shield,
                drawerMap.Document,
                drawerMap.Clipboard,
                showPasswordSettings ? drawerMap.Settings : drawerMap.Envelope,
                drawerMap.TextBubble,
              ].map(renderDrawerItem)}
            </List>
            <List>
              <ListIcons
                {...drawerMap.SignOut}
                key={`${drawerMap.SignOut.text}`}
                showText={open}
                path="#"
                onClick={async () => {
                  try {
                    await logoutMutation.mutateAsync();
                  } catch (err) {
                    // Error handled by mutation
                  }
                }}
              />
            </List>
          </Stack>
        </Stack>
      </Drawer>
    </AppBar>
  );
};

export default Header;
